<template>
  <div class="knowledge-page">
    <v-card class="knowledge-dialog__card knowledge-page__card">
        <div class="knowledge-dialog__hero">
          <div class="knowledge-dialog__hero-copy">
            <span class="knowledge-dialog__eyebrow">Central de ajuda</span>
            <h2>Encontre respostas com menos atrito</h2>
            <p>
              Procure por assunto, refine os resultados e abra apenas o
              conteudo que realmente faz sentido para o atendimento.
            </p>

            <div class="knowledge-dialog__quick">
              <v-chip
                v-for="atalho in atalhos"
                :key="atalho"
                small
                label
                class="knowledge-dialog__quick-chip"
                @click="aplicaAtalho(atalho)"
              >
                {{ atalho }}
              </v-chip>
            </div>

            <div class="knowledge-dialog__hero-actions">
              <v-btn
                small
                outlined
                color="primary"
                @click="abreGerenciamento"
              >
                Gerenciar artigos
              </v-btn>
              <v-btn
                v-if="modoPainel === 'gestao'"
                small
                text
                color="primary"
                @click="voltaParaBusca"
              >
                Voltar para busca
              </v-btn>
            </div>
          </div>

          <div class="knowledge-dialog__hero-robot">
            <img :src="walker" alt="" />
          </div>
        </div>

        <v-card-text class="knowledge-dialog__content">
          <template v-if="modoPainel === 'busca'">
            <div class="knowledge-toolbar">
              <div class="knowledge-toolbar__search">
                <v-text-field
                  ref="searchInput"
                  v-model="busca_conhecimento"
                  label="O que voce precisa encontrar?"
                  placeholder="Ex.: segunda via, bloqueio, erro, cliente..."
                  outlined
                  hide-details="auto"
                  prepend-inner-icon="mdi-magnify"
                  @keydown.enter.prevent="consultaBase"
                ></v-text-field>
              </div>

              <div class="knowledge-toolbar__actions">
                <v-btn
                  color="primary"
                  large
                  @click="consultaBase"
                  :loading="buscando"
                >
                  Buscar
                </v-btn>
                <v-btn outlined large color="primary" @click="abreGerenciamento">
                  Artigos
                </v-btn>
              </div>
            </div>

            <div class="knowledge-toolbar knowledge-toolbar--secondary">
              <div class="knowledge-toolbar__status">
                <strong>{{ resumoResultados }}</strong>
                <span v-if="ultimaConsulta">
                  Ultima busca: "{{ ultimaConsulta }}"
                </span>
              </div>

              <div
                v-if="jaBuscou && conhecimento.length"
                class="knowledge-toolbar__filter"
              >
                <v-text-field
                  v-model="busca_local"
                  label="Refinar nos resultados"
                  placeholder="Filtre por titulo, tag ou descricao"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-filter-variant"
                ></v-text-field>
              </div>
            </div>

            <div
              v-if="categoriasDisponiveis.length"
              class="knowledge-categories"
            >
              <v-chip
                small
                label
                :input-value="categoriaAtiva === 'Todas'"
                class="knowledge-categories__chip"
                @click="selecionaCategoria('Todas')"
              >
                Todas
              </v-chip>

              <v-chip
                v-for="categoria in categoriasDisponiveis"
                :key="categoria"
                small
                label
                :input-value="categoriaAtiva === categoria"
                class="knowledge-categories__chip"
                @click="selecionaCategoria(categoria)"
              >
                {{ categoria }}
              </v-chip>
            </div>

            <div v-if="buscando" class="knowledge-empty">
              <v-progress-circular indeterminate color="primary" size="34" />
              <strong>Buscando conhecimento</strong>
              <span>Estou organizando os resultados para voce.</span>
            </div>

            <div
              v-else-if="erroBusca"
              class="knowledge-empty knowledge-empty--error"
            >
              <v-icon color="error" size="34">mdi-alert-circle-outline</v-icon>
              <strong>Busca indisponivel</strong>
              <span>{{ erroBusca }}</span>
            </div>

            <div
              v-else-if="!jaBuscou"
              class="knowledge-empty knowledge-empty--intro"
            >
              <div class="knowledge-empty__robot">
                <img :src="walker" alt="" />
              </div>
              <strong>Digite um assunto para comecar</strong>
              <span>
                Use termos simples como cliente, bloqueio, boleto, financeiro ou
                pedido para abrir a base.
              </span>
            </div>

            <div
              v-else-if="filteredConhecimento.length === 0"
              class="knowledge-empty"
            >
              <v-icon color="primary" size="34"
                >mdi-text-box-search-outline</v-icon
              >
              <strong>Nada encontrado com esse filtro</strong>
              <span>
                Tente outro termo, remova o refinamento local ou volte para a
                tag "Todas".
              </span>
            </div>

            <v-expansion-panels
              v-else
              v-model="paineisAbertos"
              multiple
              class="knowledge-results"
            >
              <v-expansion-panel
                v-for="(item, idx) in filteredConhecimento"
                :key="resultKey(item, idx)"
                class="knowledge-result"
              >
                <v-expansion-panel-header class="knowledge-result__header">
                  <div class="knowledge-result__headline">
                    <div class="knowledge-result__title-row">
                      <strong>{{ item.titulo || "Sem titulo" }}</strong>
                    </div>
                    <div
                      v-if="item.tags_list && item.tags_list.length"
                      class="knowledge-result__tags"
                    >
                      <v-chip
                        v-for="tag in item.tags_list"
                        :key="tag"
                        x-small
                        label
                        class="knowledge-result__tag"
                      >
                        {{ tag }}
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="knowledge-result__content">
                  <div class="knowledge-result__body">
                    <vue-editor
                      :id="`editor_base_conhecimento_view_${resultKey(item, idx)}`"
                      :value="item.descricao"
                      :disabled="true"
                      :editor-toolbar="[]"
                      :editor-options="{ modules: { toolbar: false } }"
                      class="knowledge-result__viewer"
                      @ready="preparaViewerDescricao($event, item.descricao)"
                    ></vue-editor>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>

          <div v-else class="knowledge-admin">
            <div class="knowledge-admin__form">
              <div class="knowledge-admin__header">
                <div>
                  <strong>{{
                    artigoOperacao === "A" ? "Editar artigo" : "Novo artigo"
                  }}</strong>
                  <span>
                    Cadastre titulo, descricao em HTML e quantas tags forem
                    necessarias.
                  </span>
                </div>

                <div class="knowledge-admin__header-actions">
                  <v-btn
                    text
                    color="primary"
                    class="knowledge-admin__btn-text"
                    @click="novoArtigo"
                  >
                    Limpar
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="knowledge-admin__btn-save"
                    @click="salvarArtigo"
                    :loading="salvandoArtigo"
                  >
                    Salvar
                  </v-btn>
                </div>
              </div>

              <v-text-field
                v-model="artigoForm.titulo"
                label="Titulo"
                outlined
                class="knowledge-admin__field"
                hide-details="auto"
              ></v-text-field>

              <v-combobox
                v-model="artigoForm.tags"
                :items="tagsSugestoes"
                label="Tags"
                multiple
                outlined
                class="knowledge-admin__field"
                :hide-details="true"
                :hide-no-data="true"
                :return-object="false"
              >
                <template v-slot:selection="data">
                  <v-chip
                    :key="JSON.stringify(data.item)"
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    :disabled="data.disabled"
                    small
                    label
                    close
                    @click:close="data.parent.selectItem(data.item)"
                  >
                    {{ normalizeTagLabel(data.item) }}
                  </v-chip>
                </template>
              </v-combobox>

              <div class="knowledge-admin__editor">
                <vue-editor
                  id="editor_base_conhecimento"
                  v-model="artigoForm.descricao"
                ></vue-editor>
              </div>

              <div class="knowledge-admin__meta">
                <span>ID: {{ artigoForm.id || "novo" }}</span>
                <span>
                  Data cadastro:
                  {{ artigoForm.data_cad || "sera preenchida ao salvar" }}
                </span>
              </div>
            </div>

            <div class="knowledge-admin__list">
              <div class="knowledge-admin__header">
                <div>
                  <strong>Artigos cadastrados</strong>
                  <span>{{ artigosAdmin.length }} registro(s) carregados.</span>
                </div>

                <div class="knowledge-admin__header-actions">
                  <v-text-field
                    v-model="busca_admin"
                    dense
                    outlined
                    hide-details="auto"
                    prepend-inner-icon="mdi-magnify"
                    label="Filtrar artigos"
                    class="knowledge-admin__search"
                  ></v-text-field>
                  <v-btn
                    icon
                    color="primary"
                    @click="carregaArtigosAdmin"
                    :loading="carregandoAdmin"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>
              </div>

              <v-data-table
                :headers="headersAdmin"
                :items="artigosAdminFiltrados"
                :loading="carregandoAdmin"
                fixed-header
                height="420px"
                dense
                hide-default-footer
                disable-pagination
                class="knowledge-admin__table"
              >
                <template v-slot:item.tags="{ item }">
                  <div class="knowledge-admin__tags">
                    <v-chip
                      v-for="tag in item.tags_list"
                      :key="tag"
                      x-small
                      label
                      class="knowledge-admin__tag"
                    >
                      {{ tag }}
                    </v-chip>
                  </div>
                </template>

                <template v-slot:item.acoes="{ item }">
                  <div class="knowledge-admin__actions">
                    <v-btn icon small @click="editarArtigo(item)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon small @click="excluirArtigo(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Vue from "vue";
import { VueEditor } from "vue2-editor";
import ExecutaSQL from "../domain/ExecutaSQL";

const createEmptyArtigo = () => ({
  id: "",
  titulo: "",
  descricao: "",
  data_cad: "",
  tags: [],
});

export default {
  components: {
    VueEditor,
  },
  data: () => ({
    busca_conhecimento: "",
    busca_local: "",
    busca_admin: "",
    modoPainel: "busca",
    conhecimento: [],
    artigosAdmin: [],
    categoriaAtiva: "Todas",
    paineisAbertos: [],
    buscando: false,
    jaBuscou: false,
    erroBusca: "",
    ultimaConsulta: "",
    carregandoAdmin: false,
    salvandoArtigo: false,
    artigoOperacao: "I",
    artigoForm: createEmptyArtigo(),
    headersAdmin: [
      { text: "ID", value: "id", width: "80px" },
      { text: "Titulo", value: "titulo" },
      { text: "Tags", value: "tags", sortable: false },
      { text: "Cadastro", value: "data_cad", width: "130px" },
      { text: "", value: "acoes", sortable: false, width: "96px" },
    ],
    walker: require("../assets/robo.gif"),
    atalhos: ["Bloqueio", "Cliente", "Boleto", "Financeiro", "Pedido"],
  }),
  computed: {
    categoriasDisponiveis() {
      const categorias = this.conhecimento.reduce((acc, item) => {
        const tags = item && item.tags_list ? item.tags_list : [];
        return acc.concat(tags);
      }, []);

      return [...new Set(categorias)];
    },
    filteredConhecimento() {
      const filtroLocal = this.normalizaTexto(this.busca_local);

      return this.conhecimento.filter((item) => {
        const categoria = item && item.tags_list ? item.tags_list : [];
        const matchCategoria =
          this.categoriaAtiva === "Todas" ||
          categoria.includes(this.categoriaAtiva);

        if (!matchCategoria) return false;
        if (!filtroLocal) return true;

        const baseTexto = [
          item && item.titulo ? item.titulo : "",
          categoria.join(" "),
          this.plainDescricao(item),
        ]
          .join(" ")
          .trim();

        return this.normalizaTexto(baseTexto).includes(filtroLocal);
      });
    },
    artigosAdminFiltrados() {
      const filtro = this.normalizaTexto(this.busca_admin);

      if (!filtro) return this.artigosAdmin;

      return this.artigosAdmin.filter((item) => {
        const baseTexto = [
          item.id,
          item.titulo,
          item.data_cad,
          item.tags_list.join(" "),
          this.plainDescricao(item),
        ]
          .join(" ")
          .trim();

        return this.normalizaTexto(baseTexto).includes(filtro);
      });
    },
    tagsSugestoes() {
      const base = this.conhecimento.concat(this.artigosAdmin);
      const tags = base.reduce((acc, item) => {
        const atuais = item && item.tags_list ? item.tags_list : [];
        return acc.concat(atuais);
      }, []);

      return [...new Set(tags)].sort((a, b) => a.localeCompare(b));
    },
    resumoResultados() {
      if (this.buscando) return "Buscando respostas...";
      if (this.erroBusca) return "Nao consegui carregar a ajuda.";
      if (!this.jaBuscou)
        return "Abra a busca e procure pelo assunto do chamado.";

      const total = this.filteredConhecimento.length;
      const base = this.conhecimento.length;

      if (base === 0) return "Nenhum resultado encontrado para essa busca.";
      if (total !== base) return `${total} resultado(s) apos refinamento.`;

      return `${total} resultado(s) encontrado(s).`;
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (
        this.$refs.searchInput &&
        typeof this.$refs.searchInput.focus === "function"
      ) {
        this.$refs.searchInput.focus();
      }
    });
  },
  methods: {
    abreGerenciamento() {
      this.modoPainel = "gestao";
      this.carregaArtigosAdmin();
    },
    voltaParaBusca() {
      this.modoPainel = "busca";
      this.$nextTick(() => {
        if (
          this.$refs.searchInput &&
          typeof this.$refs.searchInput.focus === "function"
        ) {
          this.$refs.searchInput.focus();
        }
      });
    },
    aplicaAtalho(atalho) {
      this.modoPainel = "busca";
      this.busca_conhecimento = atalho;
      this.consultaBase();
    },
    selecionaCategoria(categoria) {
      this.categoriaAtiva = categoria;
    },
    resultKey(item, idx) {
      return [
        item && item.id != null ? item.id : "sem-id",
        item && item.titulo ? item.titulo : "sem-titulo",
        idx,
      ].join("-");
    },
    normalizaTexto(valor) {
      return valor == null
        ? ""
        : String(valor)
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim();
    },
    escapeHtml(valor) {
      return String(valor)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    decodeHtmlEntities(valor) {
      if (valor == null || valor === "") return "";

      let atual = String(valor);
      let anterior = "";
      let tentativas = 0;
      const entidadesComuns = {
        nbsp: " ",
        aacute: "á",
        eacute: "é",
        iacute: "í",
        oacute: "ó",
        uacute: "ú",
        agrave: "à",
        egrave: "è",
        igrave: "ì",
        ograve: "ò",
        ugrave: "ù",
        acirc: "â",
        ecirc: "ê",
        icirc: "î",
        ocirc: "ô",
        ucirc: "û",
        atilde: "ã",
        otilde: "õ",
        ccedil: "ç",
        Aacute: "Á",
        Eacute: "É",
        Iacute: "Í",
        Oacute: "Ó",
        Uacute: "Ú",
        Agrave: "À",
        Egrave: "È",
        Igrave: "Ì",
        Ograve: "Ò",
        Ugrave: "Ù",
        Acirc: "Â",
        Ecirc: "Ê",
        Icirc: "Î",
        Ocirc: "Ô",
        Ucirc: "Û",
        Atilde: "Ã",
        Otilde: "Õ",
        Ccedil: "Ç",
      };
      const decodeEntidade = (entidade) => {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = entidade;
        return textarea.value;
      };
      const decodeEntidadesComuns = (texto) => {
        let resultado = texto;

        Object.keys(entidadesComuns).forEach((chave) => {
          const valorEntidade = entidadesComuns[chave];
          resultado = resultado.replace(new RegExp(`&${chave};`, "g"), valorEntidade);
          resultado = resultado.replace(new RegExp(`${chave};`, "g"), valorEntidade);
        });

        return resultado;
      };

      // Alguns registros antigos chegam com entidades escapadas mais de uma vez.
      while (atual !== anterior && tentativas < 5) {
        anterior = atual;
        atual = atual.replace(
          /&(?:#\d+|#x[a-f\d]+|[a-z\d]+);/gi,
          (entidade) => decodeEntidade(entidade)
        );
        tentativas += 1;
      }

      return decodeEntidadesComuns(atual);
    },
    repairResidualEntities(valor) {
      if (valor == null || valor === "") return "";

      return String(valor)
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, '"')
        .replace(/&apos;/gi, "'")
        .replace(/\blt;/gi, "<")
        .replace(/\bgt;/gi, ">")
        .replace(/\bamp;/gi, "&")
        .replace(/\bquot;/gi, '"')
        .replace(/\bapos;/gi, "'")
        .replace(/\bnbsp;/gi, " ");
    },
    decodeUnicodeEscapes(valor) {
      if (valor == null || valor === "") return "";

      return String(valor)
        .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
          String.fromCharCode(parseInt(hex, 16))
        )
        .replace(/\\x([0-9a-fA-F]{2})/g, (_, hex) =>
          String.fromCharCode(parseInt(hex, 16))
        );
    },
    fixMojibakeUtf8(valor) {
      if (valor == null || valor === "") return "";

      const texto = String(valor);
      if (!/[ÃÂâ€]/.test(texto)) return texto;

      try {
        const bytes = Uint8Array.from(texto, (char) => char.charCodeAt(0));
        return new TextDecoder("utf-8").decode(bytes);
      } catch (err) {
        void err;
        return texto;
      }
    },
    normalizaDescricaoBruta(valor) {
      let atual = valor == null ? "" : String(valor).trim();
      let anterior = "";
      let tentativas = 0;

      while (atual !== anterior && tentativas < 5) {
        anterior = atual;
        atual = atual
          .replace(/\\"/g, '"')
          .replace(/\\'/g, "'")
          .replace(/\\r/g, "\r")
          .replace(/\\n/g, "\n");
        atual = this.decodeUnicodeEscapes(atual);
        atual = this.decodeHtmlEntities(atual);
        atual = this.repairResidualEntities(atual);
        atual = this.fixMojibakeUtf8(atual);
        tentativas += 1;
      }

      return atual;
    },
    safeDecodeBase64(valor) {
      try {
        return decodeURIComponent(window.escape(window.atob(valor)));
      } catch (err) {
        void err;
        return Vue.atob(valor);
      }
    },
    safeEncodeBase64(valor) {
      try {
        return window.btoa(window.unescape(encodeURIComponent(valor)));
      } catch (err) {
        void err;
        return Vue.btoa(valor);
      }
    },
    decodeMaybeBase64(value) {
      if (value == null || value === "") return "";
      if (typeof value !== "string") return value;

      const trimmed = value.trim();
      const normalized = trimmed.replace(/\s+/g, "");
      const looksLikeBase64 =
        normalized.length >= 8 &&
        /^[A-Za-z0-9+/=]+$/.test(normalized);

      if (!looksLikeBase64) return value;

      try {
        const padding = (4 - (normalized.length % 4 || 4)) % 4;
        const padded = normalized + "=".repeat(padding);
        const decoded = this.safeDecodeBase64(padded);
        if (/[\x00-\x08\x0E-\x1F]/.test(decoded)) return value;
        return decoded || value;
      } catch (err) {
        void err;
        return value;
      }
    },
    descricaoParaHtml(valor) {
      const descricao = this.normalizaDescricaoBruta(valor);
      if (!descricao) return "";

      if (/<\/?[a-z][\s\S]*>/i.test(descricao)) return descricao;

      return this.escapeHtml(descricao).replace(/\r?\n/g, "<br>");
    },
    normalizeTagLabel(tag) {
      if (tag == null) return "";

      let value = String(tag).trim();

      if (
        (value.startsWith("[") && value.endsWith("]")) ||
        (value.startsWith("{") && value.endsWith("}"))
      ) {
        value = value.slice(1, -1).trim();
      }

      value = value
        .replace(/^"+|"+$/g, "")
        .replace(/^'+|'+$/g, "")
        .replace(/\s+/g, " ")
        .trim();

      return value;
    },
    parseTags(tags) {
      if (tags == null || tags === "") return [];
      if (Array.isArray(tags)) {
        return [
          ...new Set(
            tags
              .map((item) => this.normalizeTagLabel(item))
              .filter(Boolean)
          ),
        ];
      }

      if (typeof tags === "string") {
        try {
          const parsed = JSON.parse(tags);
          if (Array.isArray(parsed)) {
            return [
              ...new Set(
                parsed
                  .map((item) => this.normalizeTagLabel(item))
                  .filter(Boolean)
              ),
            ];
          }
        } catch (err) {
          void err;
        }

        const trimmed = tags.trim();
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
          return [
            ...new Set(
              trimmed
                .slice(1, -1)
                .split(",")
                .map((item) => this.normalizeTagLabel(item))
                .filter(Boolean)
            ),
          ];
        }

        return [
          ...new Set(
            tags
              .split(/[;,]/)
              .map((item) => this.normalizeTagLabel(item))
              .filter(Boolean)
          ),
        ];
      }

      return [];
    },
    stringifyTags(tags) {
      return JSON.stringify(this.parseTags(tags));
    },
    normalizaArtigo(item) {
      const base = item || {};
      const descricaoDecode = this.decodeMaybeBase64(base.descricao || "");
      const tagsList = this.parseTags(base.tags || base.nome_cat || "");

      return Object.assign({}, base, {
        id: base.id == null ? "" : base.id,
        titulo: base.titulo == null ? "" : String(base.titulo),
        descricao: this.descricaoParaHtml(descricaoDecode),
        data_cad: base.data_cad || base.datac || "",
        tags: this.stringifyTags(tagsList),
        tags_list: tagsList,
      });
    },
    normalizaListaArtigos(lista) {
      return (Array.isArray(lista) ? lista : []).map((item) =>
        this.normalizaArtigo(item)
      );
    },
    plainDescricao(item) {
      const descricao = item && item.descricao ? String(item.descricao) : "";
      return this.normalizaDescricaoBruta(
        descricao
          .replace(/<style[\s\S]*?<\/style>/gi, " ")
          .replace(/<script[\s\S]*?<\/script>/gi, " ")
          .replace(/<br\s*\/?>/gi, " ")
          .replace(/<\/p>/gi, " ")
          .replace(/<[^>]*>/g, " ")
          .replace(/&nbsp;/gi, " ")
          .replace(/\s+/g, " ")
          .trim()
      );
    },
    previewDescricao(item) {
      const texto = this.plainDescricao(item);
      if (!texto) return "Abra para visualizar o conteudo completo.";
      return texto.length > 180 ? texto.slice(0, 180).trim() + "..." : texto;
    },
    preparaViewerDescricao(editor, descricao) {
      if (!editor || !editor.clipboard) return;

      const html = this.descricaoParaHtml(descricao);
      editor.setText("");
      editor.clipboard.dangerouslyPasteHTML(html || "<p><br></p>");
      editor.enable(false);
    },
    sqlEscape(value) {
      if (value == null) return "";
      return String(value).replace(/'/g, "''");
    },
    sqlString(value) {
      return `'${this.sqlEscape(value)}'`;
    },
    getSqlService() {
      return new ExecutaSQL(this.$resource);
    },
    getSqlSelectBaseArtigo() {
      return [
        "SELECT id, COALESCE(titulo,'') AS titulo, COALESCE(descricao,'') AS descricao,",
        "COALESCE(data_cad::text,'') AS data_cad, COALESCE(tags,'') AS tags",
        "FROM base_artigo",
        "ORDER BY id DESC",
      ].join(" ");
    },
    executaSqlConsulta(sql) {
      return this.getSqlService().get(sql);
    },
    executaSqlComando(sql) {
      return this.getSqlService().get(sql);
    },
    novoArtigo() {
      this.artigoOperacao = "I";
      this.artigoForm = createEmptyArtigo();
    },
    editarArtigo(item) {
      const artigo = this.normalizaArtigo(item);
      this.artigoOperacao = "A";
      this.artigoForm = {
        id: artigo.id,
        titulo: artigo.titulo,
        descricao: artigo.descricao,
        data_cad: artigo.data_cad,
        tags: artigo.tags_list.slice(),
      };
    },
    editorTemConteudo(html) {
      const texto = (html || "")
        .replace(/<p><br><\/p>/gi, "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim();
      return texto !== "";
    },
    normalizeDateForSql(value) {
      if (value == null || value === "") return Vue.getDataInput();

      const text = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return Vue.fData2(text);
      if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return text.slice(0, 10);

      return Vue.getDataInput();
    },
    buildPayloadArtigo() {
      return {
        id: this.artigoForm.id || "",
        titulo: (this.artigoForm.titulo || "").trim(),
        descricao: this.safeEncodeBase64(this.artigoForm.descricao || ""),
        data_cad: this.normalizeDateForSql(this.artigoForm.data_cad),
        tags: this.stringifyTags(this.artigoForm.tags),
      };
    },
    parseSqlId(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    buildInsertSqlArtigo(payload) {
      return [
        "INSERT INTO base_artigo (titulo, descricao, data_cad, tags)",
        "VALUES (",
        this.sqlString(payload.titulo),
        ", ",
        this.sqlString(payload.descricao),
        ", ",
        this.sqlString(payload.data_cad),
        ", ",
        this.sqlString(payload.tags),
        ")",
      ].join("");
    },
    buildUpdateSqlArtigo(payload) {
      const id = this.parseSqlId(payload.id);
      return [
        "UPDATE base_artigo SET ",
        "titulo = ",
        this.sqlString(payload.titulo),
        ", descricao = ",
        this.sqlString(payload.descricao),
        ", data_cad = ",
        this.sqlString(payload.data_cad),
        ", tags = ",
        this.sqlString(payload.tags),
        " WHERE id = ",
        String(id),
      ].join("");
    },
    filtraArtigosPorBusca(lista, termo) {
      const filtroBusca = this.normalizaTexto(termo);
      if (!filtroBusca) return lista;

      return lista.filter((item) => {
        const baseTexto = [
          item && item.titulo ? item.titulo : "",
          item && item.tags_list ? item.tags_list.join(" ") : "",
          this.plainDescricao(item),
        ]
          .join(" ")
          .trim();

        return this.normalizaTexto(baseTexto).includes(filtroBusca);
      });
    },
    salvarArtigo() {
      if ((this.artigoForm.titulo || "").trim() === "") {
        Vue.Mensagem(this, "Titulo", "Preencha o titulo do artigo");
        return;
      }

      if (!this.editorTemConteudo(this.artigoForm.descricao)) {
        Vue.Mensagem(this, "Descricao", "Preencha a descricao do artigo");
        return;
      }

      const payload = this.buildPayloadArtigo();
      const sql =
        this.artigoOperacao === "A"
          ? this.buildUpdateSqlArtigo(payload)
          : this.buildInsertSqlArtigo(payload);

      this.salvandoArtigo = true;
      Vue.Loading("Salvando artigo...", "", this);

      this.executaSqlComando(sql).then(
        () => {
          this.salvandoArtigo = false;
          Vue.CloseLoading(this);
          this.novoArtigo();
          this.carregaArtigosAdmin();
          this.atualizaBuscaAtual();
        },
        (err) => {
          this.salvandoArtigo = false;
          Vue.CloseLoading(this);
          console.log("SQL base_artigo:", sql);
          Vue.Mensagem(this, "Erro", err);
        }
      );
    },
    excluirArtigo(item) {
      Vue.Pergunta(
        this,
        "Excluir artigo",
        `Deseja excluir o artigo "${item.titulo || "Sem titulo"}"?`,
        () => {
          Vue.Loading("Excluindo artigo...", "", this);
          const id = this.parseSqlId(item.id);
          if (!id) {
            Vue.CloseLoading(this);
            Vue.Mensagem(this, "Excluir artigo", "ID do artigo invalido");
            return;
          }
          const sql =
            "DELETE FROM base_artigo WHERE id = " + String(id);
          this.executaSqlComando(sql).then(
            () => {
              Vue.CloseLoading(this);
              if (String(this.artigoForm.id) === String(item.id)) {
                this.novoArtigo();
              }
              this.carregaArtigosAdmin();
              this.atualizaBuscaAtual();
            },
            (err) => {
              Vue.CloseLoading(this);
              Vue.Mensagem(this, "Erro", err);
            }
          );
        },
        null
      );
    },
    carregaArtigosAdmin() {
      if (this.carregandoAdmin) return;

      this.carregandoAdmin = true;
      this.executaSqlConsulta(this.getSqlSelectBaseArtigo()).then(
        (ret) => {
          this.artigosAdmin = this.normalizaListaArtigos(ret);
          this.carregandoAdmin = false;
        },
        (err) => {
          this.artigosAdmin = [];
          this.carregandoAdmin = false;
          Vue.Mensagem(
            this,
            "Base de artigos",
            err && err.message
              ? err.message
              : "Nao foi possivel carregar os artigos cadastrados"
          );
        }
      );
    },
    atualizaBuscaAtual() {
      const termo =
        this.busca_conhecimento == null ? "" : this.busca_conhecimento.trim();

      if (!termo) return;

      this.executaSqlConsulta(this.getSqlSelectBaseArtigo()).then(
        (ret) => {
          const lista = this.normalizaListaArtigos(ret);
          this.conhecimento = this.filtraArtigosPorBusca(lista, termo);
          this.paineisAbertos = this.conhecimento.length ? [0] : [];
        },
        (err) => {
          console.log(err);
        }
      );
    },
    consultaBase() {
      const termo =
        this.busca_conhecimento == null ? "" : this.busca_conhecimento.trim();

      this.modoPainel = "busca";
      this.erroBusca = "";

      if (!termo) {
        this.jaBuscou = false;
        this.conhecimento = [];
        this.paineisAbertos = [];
        return;
      }

      this.buscando = true;
      this.categoriaAtiva = "Todas";
      this.busca_local = "";
      this.ultimaConsulta = termo;
      this.executaSqlConsulta(this.getSqlSelectBaseArtigo()).then(
        (ret) => {
          const lista = this.normalizaListaArtigos(ret);
          this.conhecimento = this.filtraArtigosPorBusca(lista, termo);
          this.jaBuscou = true;
          this.paineisAbertos = this.conhecimento.length ? [0] : [];
          this.buscando = false;
        },
        (err) => {
          this.conhecimento = [];
          this.paineisAbertos = [];
          this.jaBuscou = true;
          this.erroBusca =
            err && err.message
              ? err.message
              : "Nao foi possivel consultar a base agora.";
          this.buscando = false;
        }
      );
    },
  },
};
</script>

<style>
.knowledge-page {
  padding: 24px;
}

.knowledge-page__card {
  max-width: 1360px;
  margin: 0 auto;
}

.knowledge-fab {
  position: fixed;
  z-index: 12;
  display: grid;
  grid-template-columns: 68px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  width: 248px;
  padding: 14px 18px 14px 14px;
  border-radius: 24px;
  border: 1px solid rgba(15, 76, 92, 0.14);
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.96) 0%,
    rgba(239, 246, 255, 0.98) 100%
  );
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.14);
  cursor: grab;
  user-select: none;
  backdrop-filter: blur(18px);
}

.knowledge-fab--dragging {
  cursor: grabbing;
  box-shadow: 0 28px 52px rgba(15, 23, 42, 0.2);
}

.knowledge-fab--open {
  border-color: rgba(15, 76, 92, 0.24);
}

.knowledge-fab__grab {
  position: absolute;
  top: 10px;
  right: 12px;
  color: #64748b;
}

.knowledge-fab__robot-shell {
  display: grid;
  place-items: center;
  width: 68px;
  height: 68px;
  border-radius: 20px;
  background: linear-gradient(
    180deg,
    rgba(15, 76, 92, 0.1) 0%,
    rgba(20, 184, 166, 0.08) 100%
  );
  overflow: hidden;
}

.knowledge-fab__robot {
  width: 62px;
  height: 62px;
  object-fit: contain;
  mix-blend-mode: multiply;
  filter: grayscale(1) contrast(1.14) brightness(0.82);
}

.knowledge-fab__copy strong {
  display: block;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
}

.knowledge-fab__copy span {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
}

.knowledge-dialog {
  box-shadow: none !important;
}

.knowledge-dialog__card {
  overflow: hidden;
  border-radius: 28px !important;
}

.knowledge-dialog__hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  gap: 18px;
  padding: 28px 30px 22px;
  color: #e2e8f0;
  background:
    radial-gradient(circle at top left, rgba(103, 183, 204, 0.22), transparent 26%),
    radial-gradient(circle at 80% 20%, rgba(45, 212, 191, 0.16), transparent 24%),
    linear-gradient(145deg, #0a3440 0%, #0f4c5c 56%, #146c7d 100%);
}

.knowledge-dialog__eyebrow {
  display: inline-block;
  margin-bottom: 10px;
  color: rgba(226, 232, 240, 0.8);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.knowledge-dialog__hero-copy h2 {
  margin: 0;
  color: #f8fafc;
  font-size: 2rem;
  line-height: 1.02;
  letter-spacing: -0.04em;
}

.knowledge-dialog__hero-copy p {
  max-width: 56ch;
  margin: 12px 0 0;
  color: rgba(226, 232, 240, 0.8);
  line-height: 1.72;
}

.knowledge-dialog__quick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.knowledge-dialog__quick-chip {
  background: rgba(255, 255, 255, 0.14) !important;
  color: #f8fafc !important;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.knowledge-dialog__hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.knowledge-dialog__hero-robot {
  display: flex;
  align-items: center;
  justify-content: center;
}

.knowledge-dialog__hero-robot img {
  width: 190px;
  max-width: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
  filter: grayscale(1) contrast(1.16) brightness(1.45);
}

.knowledge-dialog__close {
  position: absolute !important;
  top: 18px;
  right: 18px;
  color: #f8fafc !important;
}

.knowledge-dialog__content {
  padding: 22px 24px 24px !important;
}

.knowledge-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
}

.knowledge-toolbar__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.knowledge-toolbar--secondary {
  margin-top: 12px;
}

.knowledge-toolbar__status {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.knowledge-toolbar__status strong {
  color: #0f172a;
  font-size: 0.98rem;
}

.knowledge-toolbar__status span {
  color: #64748b;
  font-size: 0.84rem;
}

.knowledge-toolbar__filter {
  min-width: 340px;
}

.knowledge-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 10px;
}

.knowledge-categories__chip {
  border: 1px solid rgba(15, 76, 92, 0.14);
}

.knowledge-categories__chip.v-chip--active,
.knowledge-categories__chip.v-chip--input-value {
  background: rgba(15, 76, 92, 0.12) !important;
  color: #0f4c5c !important;
}

.knowledge-empty {
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 46px 24px;
  text-align: center;
  border: 1px dashed rgba(148, 163, 184, 0.24);
  border-radius: 24px;
  background: rgba(248, 250, 252, 0.74);
  margin-top: 14px;
}

.knowledge-empty strong {
  color: #0f172a;
  font-size: 1rem;
}

.knowledge-empty span {
  max-width: 48ch;
  color: #64748b;
  line-height: 1.6;
}

.knowledge-empty--intro {
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 0.94) 0%,
    rgba(240, 249, 255, 0.9) 100%
  );
}

.knowledge-empty__robot img {
  width: 126px;
  mix-blend-mode: multiply;
  filter: grayscale(1) contrast(1.14) brightness(0.82);
}

.knowledge-results {
  margin-top: 16px;
}

.knowledge-result {
  border-radius: 22px !important;
  margin-bottom: 12px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: none !important;
}

.knowledge-result::before {
  display: none;
}

.knowledge-result__header {
  padding-top: 18px !important;
  padding-bottom: 18px !important;
}

.knowledge-result__headline {
  width: 100%;
}

.knowledge-result__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.knowledge-result__title-row strong {
  color: #0f172a;
  font-size: 1rem;
}

.knowledge-result__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.knowledge-result__tag {
  background: rgba(15, 118, 110, 0.12) !important;
  color: #0f766e !important;
}

.knowledge-result__headline p {
  margin: 0;
  color: #64748b;
  line-height: 1.64;
}

.knowledge-result__content {
  background: rgba(248, 250, 252, 0.78);
}

.knowledge-result__body {
  color: #334155;
  line-height: 1.7;
}

.knowledge-result__body p:last-child {
  margin-bottom: 0;
}

.knowledge-result__body img,
.knowledge-result__body table {
  max-width: 100%;
}

.knowledge-result__viewer .ql-toolbar.ql-snow {
  display: none;
}

.knowledge-result__viewer .quillWrapper,
.knowledge-result__viewer .ql-container.ql-snow {
  border: none !important;
  background: transparent;
}

.knowledge-result__viewer .ql-editor {
  min-height: 0;
  padding: 0;
  color: #334155;
  line-height: 1.7;
}

.knowledge-admin {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: 18px;
}

.knowledge-admin__form,
.knowledge-admin__list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 24px;
  background: rgba(248, 250, 252, 0.76);
}

.knowledge-admin__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.knowledge-admin__header strong {
  display: block;
  color: #0f172a;
  font-size: 1rem;
}

.knowledge-admin__header span {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 0.84rem;
  line-height: 1.5;
}

.knowledge-admin__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.knowledge-admin__btn-save {
  color: #fff !important;
}

.knowledge-admin__btn-save .v-btn__content,
.knowledge-admin__btn-save .v-icon,
.knowledge-admin__btn-save .v-progress-circular {
  color: #fff !important;
}

.knowledge-admin__field {
  margin-top: 2px;
}

.knowledge-admin__field .v-input__slot {
  min-height: 48px;
}

.knowledge-admin__field.v-text-field--outlined .v-label {
  top: 14px;
}

.knowledge-admin__field.v-text-field--outlined.v-input--is-label-active .v-label,
.knowledge-admin__field.v-text-field--outlined.v-input--is-focused .v-label {
  transform: translateY(-24px) scale(0.75) !important;
  transform-origin: left center !important;
  background: rgba(248, 250, 252, 0.98);
  padding: 0 6px;
  margin-left: -6px;
}

.knowledge-admin__field .v-select__selections {
  min-height: 30px;
  align-items: center;
}

.knowledge-admin__search {
  min-width: 220px;
}

.knowledge-admin__editor {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 18px;
  background: #fff;
}

.knowledge-admin__editor .quillWrapper,
.knowledge-admin__editor .ql-toolbar.ql-snow,
.knowledge-admin__editor .ql-container.ql-snow {
  border: none !important;
}

.knowledge-admin__editor .ql-toolbar.ql-snow {
  border-bottom: 1px solid rgba(148, 163, 184, 0.18) !important;
}

.knowledge-admin__editor .ql-editor {
  min-height: 220px;
  max-height: 320px;
  overflow-y: auto;
  color: #334155;
}

.knowledge-admin__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  color: #64748b;
  font-size: 0.82rem;
}

.knowledge-admin__table {
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
}

.knowledge-admin__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
}

.knowledge-admin__tag {
  background: rgba(15, 76, 92, 0.1) !important;
  color: #0f4c5c !important;
}

.knowledge-admin__tags-text {
  color: #475569;
  font-size: 0.82rem;
  word-break: break-word;
}

.knowledge-admin__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

@media (max-width: 960px) {
  .knowledge-fab {
    width: 214px;
    grid-template-columns: 60px minmax(0, 1fr);
    gap: 12px;
    padding-inline: 12px 16px;
  }

  .knowledge-fab__robot-shell {
    width: 60px;
    height: 60px;
  }

  .knowledge-dialog__hero {
    grid-template-columns: 1fr;
  }

  .knowledge-dialog__hero-robot {
    justify-content: flex-start;
  }

  .knowledge-toolbar,
  .knowledge-toolbar--secondary {
    grid-template-columns: 1fr;
  }

  .knowledge-toolbar__filter {
    min-width: 0;
  }

  .knowledge-admin {
    grid-template-columns: 1fr;
  }

  .knowledge-admin__header {
    flex-direction: column;
  }

  .knowledge-admin__header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .knowledge-admin__search {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .knowledge-fab {
    width: 198px;
    padding-right: 14px;
  }

  .knowledge-fab__copy span {
    font-size: 0.76rem;
  }

  .knowledge-dialog__hero {
    padding: 24px 18px 18px;
  }

  .knowledge-dialog__hero-copy h2 {
    font-size: 1.7rem;
  }

  .knowledge-dialog__content {
    padding: 18px 14px 18px !important;
  }

  .knowledge-result__title-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .knowledge-toolbar__actions {
    justify-content: stretch;
  }

  .knowledge-toolbar__actions .v-btn {
    width: 100%;
  }

  .knowledge-dialog__hero-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .knowledge-admin__form,
  .knowledge-admin__list {
    padding: 14px;
  }
}
</style>
